import { Component, inject, input, output, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Verbatim port of the AngularJS `goalType` constant
 * (account-v1/src/components/goals/constants/goalType.js). Maps a goal-name key
 * to its numeric value; the values key `NAME_FORMATS` (mirroring `goalName.js`).
 */
export const GOAL_TYPE: Record<string, number> = {
  SpreadTheWord: 0,
  GetSocialConnections: 1,
  GetUniqueVisitors: 2,
  SignupAndGet: 3,
  SubmitQuoteAndGet: 4,
  ShareAndGet: 5,
  SubmitPhotoAndGet: 6,
  PhotoContestWinner: 7,
  AttendeeSignup: 8,
};

/**
 * `nameFormats` from `goalName.js`: goalType VALUE → translation key
 * (`'flipto.components.goals.' + key`). Built once, exactly as the filter does
 * via `angular.forEach(goalType, ...)`.
 */
const NAME_FORMATS: Record<number, string> = (() => {
  const map: Record<number, string> = {};
  Object.keys(GOAL_TYPE).forEach((key) => {
    map[GOAL_TYPE[key]] = 'flipto.components.goals.' + key;
  });
  return map;
})();

const DEFAULT_CULTURE = 'en';

export interface GoalDescriptor {
  type: number;
  value?: number;
}

/**
 * Verbatim port of the `goalDescriptor` factory
 * (account-v1/.../goals/factories/goalDescriptor.js). Maps a goal's DB `type`
 * to a descriptor `{ type: <goalType value>, value? }` used by `goalName`.
 * Exported (WS3 goal-selectors increment) so the native `FtGoalSelectorComponent`
 * re-derives the descriptor imperatively (mirroring the v1 deep `$watch`).
 */
export function goalDescriptor(goal: any): GoalDescriptor | undefined {
  return (
    getAdvocateDescriptor(goal) ||
    getSocialConnectionsDescriptor(goal) ||
    getSharingDescriptor(goal) ||
    getParticipationDescriptor(goal) ||
    getSubmitPhotoDescriptor(goal) ||
    getPhotoContestDescriptor(goal) ||
    getGroupAdvocacyAttendeeSignupDescriptor(goal)
  );
}

function getAdvocateDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 1) {
    const descriptor: GoalDescriptor = { type: GOAL_TYPE['SpreadTheWord'] };
    const clicks = parseInt(goal.clicks, 10);
    const signups = parseInt(goal.signups, 10);
    if (clicks > 0) {
      descriptor.type = GOAL_TYPE['GetUniqueVisitors'];
      descriptor.value = clicks;
    } else if (signups > 0) {
      descriptor.type = GOAL_TYPE['GetSocialConnections'];
      descriptor.value = signups;
    }
    return descriptor;
  }
  return undefined;
}

function getSocialConnectionsDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 2) return { type: GOAL_TYPE['SignupAndGet'] };
  return undefined;
}

function getSharingDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 4) return { type: GOAL_TYPE['ShareAndGet'] };
  return undefined;
}

function getParticipationDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 3) return { type: GOAL_TYPE['SubmitQuoteAndGet'] };
  return undefined;
}

function getSubmitPhotoDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 6) return { type: GOAL_TYPE['SubmitPhotoAndGet'] };
  return undefined;
}

function getPhotoContestDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 5) return { type: GOAL_TYPE['PhotoContestWinner'] };
  return undefined;
}

function getGroupAdvocacyAttendeeSignupDescriptor(goal: any): GoalDescriptor | undefined {
  if (goal.type == 7) return { type: GOAL_TYPE['AttendeeSignup'] };
  return undefined;
}

/**
 * Native port of the AngularJS `goalName` filter
 * (account-v1/.../goals/filters/goalName.js). Resolves a goal's display label:
 * `goalDescriptor(goal)` → translate(`NAME_FORMATS[descriptor.type]`) →
 * substitute the first `#` with `descriptor.value` when present. Translate
 * resolves via native ngx (byte-identical to the drained `$filter('translate')`;
 * the format keys carry no surrounding whitespace so no `.trim()` is needed).
 */
@Pipe({ name: 'goalName', standalone: true })
export class GoalNamePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(goal: any): string {
    if (!goal) return '';
    const descriptor = goalDescriptor(goal);
    if (!descriptor) return '';
    const format: string = this.translate.instant(NAME_FORMATS[descriptor.type]);
    if (descriptor.value !== undefined) {
      return format.replace('#', String(descriptor.value));
    }
    return format;
  }
}

/**
 * Native port of the AngularJS `goalDescription` filter wrapped by the
 * `ftGoalsPanel` directive's `getGoalDescription` scope method
 * (account-v1/.../goals/filters/goalDescription.js + goalsPanel.js). Picks the
 * goal's per-language `formalText` by culture → 'en' fallback (faithful to v1:
 * `culture` is the `model.language` OBJECT, so the by-culture match never hits
 * a string `code` and it always falls back to 'en' — preserve-bug), else the
 * "description is not available" translation. Returned as `SafeHtml` via
 * `bypassSecurityTrustHtml`, mirroring v1's `$sce.trustAsHtml` (raw, unsanitised
 * — the read-view binds it with `[innerHTML]`).
 */
@Pipe({ name: 'goalDescription', standalone: true })
export class GoalDescriptionPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);

  transform(goal: any, culture?: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.getDescription(goal, culture));
  }

  private getDescription(goal: any, culture: any): string {
    if (goal && goal.languages) {
      const items = goal.languages.item || [];
      const language =
        items.find((l: any) => l && l.code === culture) || items.find((l: any) => l && l.code === DEFAULT_CULTURE);
      if (language) return language.formalText;
    }
    return this.translate.instant('flipto.components.goals.descriptionIsNotAvailable');
  }
}

/**
 * Native read-view port of the AngularJS `ftGoalsPanel` directive's READ panel:
 * the friends-goals summary `.ft-panel` (heading + per-goal `goalName` label +
 * `goalDescription` body). Clicking it emits `showModal` so the host opens the
 * native goals editor.
 */
@Component({
    selector: 'ft-goals-panel-ng',
    imports: [TranslateModule, GoalNamePipe, GoalDescriptionPipe],
    templateUrl: './ft-goals-panel.component.html'
})
export class FtGoalsPanelComponent {
    readonly goals = input<any[] | undefined>();
    readonly propertyIncentives = input<any>();
    readonly culture = input<any>();
    readonly type = input<any>();
    /** Emitted on panel click — the host opens the native `contestGoalsModal`. */
    readonly showModal = output<void>();
}
